import * as React from 'react';
import {
    ShareSmallColored,
    EmailOutlineSmallColored,
    BinOutlineColored
} from './MenuShareModalButton.styles';
import {
    ListingSharing,
    ShareLinkButton,
    ShareList,
    ShareListItem
} from '@zoopla/listings/share';
import { ListingUris, PublicationStatus } from '../../graphql.generated';
import { IconButton } from '@zoopla-eng/zds-components/button';
import { MenuMoreVerticalSmall } from '@zoopla-eng/zds-components/icons';
import { Dialog } from '@zoopla-eng/zds-components/dialog';
import { styled } from '@zoopla-eng/zds-components/themes';

const MenuButtonWrapper = styled('div', {
    position: 'relative',
    zIndex: 1
});

type ListingCardMiniMenuButtonProps = {
    testId: string;
    trackOpenMenuModal: () => void;
    trackCloseMenuModal: () => void;
    trackOpenShareModal: () => void;
    trackCloseShareModal: () => void;
    trackContactAgentClick: (listingId: string) => void;
    deleteListing: () => void;
    listingId: string;
    title: string;
    publicationStatus: PublicationStatus;
    listingUris: ListingUris;
    contactLink: string;
    shortDisplayAddress: string | null | undefined;
};

export const ListingCardMiniMenuButton = ({
    testId,
    trackOpenMenuModal,
    trackCloseMenuModal,
    trackOpenShareModal,
    trackCloseShareModal,
    trackContactAgentClick,
    deleteListing,
    listingId,
    title,
    publicationStatus,
    listingUris,
    contactLink,
    shortDisplayAddress
}: ListingCardMiniMenuButtonProps) => {
    const shareModal = (
        <Dialog
            label={`Share ${shortDisplayAddress ?? 'property'}`}
            title="Share this property with your friends and family"
            variant="bottomSheet"
            trigger={
                <ShareListItem>
                    <ShareLinkButton
                        data-testid={`mobile-menu-share-button-${listingId}`}
                    >
                        <ShareSmallColored />
                        <span>Share</span>
                    </ShareLinkButton>
                </ShareListItem>
            }
            onOpenChange={(open) =>
                open ? trackOpenShareModal() : trackCloseShareModal()
            }
        >
            <ListingSharing
                listingId={listingId}
                title={title}
                listingUrl={listingUris.detail || ''}
                sharingCategory="User Account"
            />
        </Dialog>
    );

    const mobileMenuContent = (
        <ShareList>
            {publicationStatus === PublicationStatus.Expired ? null : (
                <>
                    {shareModal}
                    <ShareListItem>
                        <ShareLinkButton
                            href={contactLink}
                            onClick={(event: React.MouseEvent<HTMLElement>) => {
                                event.stopPropagation();
                                trackContactAgentClick(listingId);
                            }}
                        >
                            <EmailOutlineSmallColored />
                            <span>Contact agent</span>
                        </ShareLinkButton>
                    </ShareListItem>
                </>
            )}
            <ShareListItem>
                <ShareLinkButton
                    data-testid={`delete-button-${listingId}`}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                        event.stopPropagation();
                        deleteListing();
                    }}
                >
                    <BinOutlineColored />
                    <span>Remove from saved</span>
                </ShareLinkButton>
            </ShareListItem>
        </ShareList>
    );

    return (
        <Dialog
            label={`Options for ${
                shortDisplayAddress || `listing ${listingId}`
            }`}
            onOpenChange={(open) =>
                open ? trackOpenMenuModal() : trackCloseMenuModal()
            }
            trigger={
                <MenuButtonWrapper>
                    <IconButton
                        category="tertiary"
                        data-testid={testId}
                        label={`Open options for ${
                            shortDisplayAddress || `listing ${listingId}`
                        }`}
                    >
                        <MenuMoreVerticalSmall />
                    </IconButton>
                </MenuButtonWrapper>
            }
            variant="bottomSheet"
        >
            {mobileMenuContent}
        </Dialog>
    );
};
